import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Swal from "sweetalert2";
import MainLayout from "../../components/layout/MainLayout";
import CommonModal from "../../components/common/CommonModal";
import CommonTable from "../../components/common/CommonTable";
import { useAuth } from "../../contexts/AuthContext";
import api from "../../services/api";
import "./EmployeeDocuments.css";

function EmployeeDocuments() {
  const { user } = useAuth();
  const fileInputRefs = useRef({});

  const [loading, setLoading] = useState(true);
  const [savingType, setSavingType] = useState("");
  const [reviewingId, setReviewingId] = useState(null);
  const [meta, setMeta] = useState({
    document_types: [],
    employees: [],
    can_manage_company_documents: false,
    can_review_company_documents: false,
  });
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
  const [documentData, setDocumentData] = useState({
    employee: null,
    documents: [],
    summary: null,
  });
  const [statusFilter, setStatusFilter] = useState("all");
  const [previewDocument, setPreviewDocument] = useState(null);
  const [reviewQueue, setReviewQueue] = useState([]);
  const [queueMeta, setQueueMeta] = useState(null);

  const canManageCompanyDocuments = !!meta.can_manage_company_documents;
  const canReviewCompanyDocuments = !!meta.can_review_company_documents;

  const selectedEmployeeLabel = useMemo(() => {
    if (!documentData.employee) return "Employee";
    return `${documentData.employee.name} (${documentData.employee.code})`;
  }, [documentData.employee]);

  const filteredDocuments = useMemo(() => {
    if (statusFilter === "all") {
      return documentData.documents;
    }

    return documentData.documents.filter((row) => {
      const status = row.document?.approval_status || "missing";
      return status === statusFilter;
    });
  }, [documentData.documents, statusFilter]);

  const fetchDocuments = useCallback(async (employeeId) => {
    setLoading(true);
    try {
      const response = await api.get("/employee-documents", {
        params: { user_id: employeeId },
      });
      if (response.data.success) {
        setDocumentData(
          response.data.data || { employee: null, documents: [], summary: null },
        );
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Failed to load employee documents.",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchReviewQueue = useCallback(async () => {
    try {
      const response = await api.get("/employee-documents/review-queue");
      if (response.data.success) {
        setReviewQueue(response.data.data || []);
        setQueueMeta(response.data.meta || null);
      }
    } catch (error) {
      console.error("Failed to load document review queue", error);
    }
  }, []);

  const fetchMeta = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("/employee-documents/meta");
      if (response.data.success) {
        const data = response.data.data || {};
        setMeta(data);
        const defaultEmployeeId =
          data.can_manage_company_documents && data.employees?.length
            ? String(data.employees[0].id)
            : String(user?.id || "");
        setSelectedEmployeeId(defaultEmployeeId);
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load employee documents.",
      });
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchMeta();
  }, [fetchMeta]);

  useEffect(() => {
    if (!selectedEmployeeId) return;
    fetchDocuments(selectedEmployeeId);
  }, [fetchDocuments, selectedEmployeeId]);

  useEffect(() => {
    if (!canReviewCompanyDocuments) return;
    fetchReviewQueue();
  }, [canReviewCompanyDocuments, fetchReviewQueue]);

  const openFilePicker = (documentType) => {
    fileInputRefs.current[documentType]?.click();
  };

  const handleFileSelected = async (documentType, event) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file || !selectedEmployeeId) return;

    const formData = new FormData();
    formData.append("document_type", documentType);
    formData.append("user_id", selectedEmployeeId);
    formData.append("file", file);

    setSavingType(documentType);
    try {
      const response = await api.post("/employee-documents", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.success) {
        Swal.fire({
          icon: "success",
          title: "Uploaded",
          text: response.data.message,
          timer: 1600,
          showConfirmButton: false,
        });
        fetchDocuments(selectedEmployeeId);
        fetchReviewQueue();
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Upload Failed",
        text:
          error.response?.data?.message ||
          "Failed to upload document. Please upload JPG, JPEG, or PDF under 1 MB.",
      });
    } finally {
      setSavingType("");
    }
  };

  const downloadDocument = async (document) => {
    try {
      const response = await api.get(`/employee-documents/${document.id}/download`, {
        responseType: "blob",
      });
      const blob = new Blob([response.data], {
        type: document.mime_type || response.headers["content-type"],
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = document.original_name || `${document.document_type}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Download Failed",
        text: error.response?.data?.message || "Failed to download document.",
      });
    }
  };

  const updateReview = async (document, approvalStatus) => {
    let rejectionReason = "";

    if (approvalStatus === "rejected") {
      const result = await Swal.fire({
        title: "Reject Document",
        input: "text",
        inputLabel: "Reason (optional)",
        inputPlaceholder: "Add a short reason for rejection",
        showCancelButton: true,
        confirmButtonText: "Reject",
        confirmButtonColor: "#dc2626",
      });

      if (!result.isConfirmed) {
        return;
      }

      rejectionReason = result.value || "";
    } else {
      const result = await Swal.fire({
        title: "Approve Document?",
        text: "This document will be marked as approved.",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Approve",
      });

      if (!result.isConfirmed) {
        return;
      }
    }

    setReviewingId(document.id);
    try {
      const response = await api.put(`/employee-documents/${document.id}/review`, {
        approval_status: approvalStatus,
        rejection_reason: rejectionReason,
      });

      if (response.data.success) {
        Swal.fire({
          icon: "success",
          title: "Updated",
          text: response.data.message,
          timer: 1500,
          showConfirmButton: false,
        });
        fetchDocuments(selectedEmployeeId);
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text: error.response?.data?.message || "Failed to update document review.",
      });
    } finally {
      setReviewingId(null);
    }
  };

  const removeDocument = async (document) => {
    const result = await Swal.fire({
      title: "Remove Document?",
      text: `${document.original_name} will be removed from this employee record.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      confirmButtonText: "Remove",
    });

    if (!result.isConfirmed) return;

    setReviewingId(document.id);
    try {
      const response = await api.delete(`/employee-documents/${document.id}`);
      if (response.data.success) {
        Swal.fire({
          icon: "success",
          title: "Removed",
          text: response.data.message,
          timer: 1500,
          showConfirmButton: false,
        });
        if (previewDocument?.id === document.id) {
          setPreviewDocument(null);
        }
        fetchDocuments(selectedEmployeeId);
        fetchReviewQueue();
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Remove Failed",
        text: error.response?.data?.message || "Failed to remove document.",
      });
    } finally {
      setReviewingId(null);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "0 KB";
    if (bytes < 1024) return `${bytes} B`;
    return `${(bytes / 1024).toFixed(1)} KB`;
  };

  const reviewQueueColumns = [
    {
      header: "Employee",
      headerClassName: "review-col-employee",
      cellClassName: "review-cell-employee",
      render: (row) => (
        <div className="review-employee-cell">
          <strong>{row.employee?.name || "N/A"}</strong>
          <span>{row.employee?.code || "N/A"}</span>
        </div>
      ),
      searchValue: (row) =>
        `${row.employee?.name || ""} ${row.employee?.code || ""} ${row.employee?.email || ""}`,
    },
    {
      header: "Document",
      headerClassName: "review-col-document",
      cellClassName: "review-cell-document",
      render: (row) => (
        <div className="review-doc-cell">
          <strong>{row.document_label}</strong>
          <span>{row.original_name}</span>
        </div>
      ),
      searchValue: (row) => `${row.document_label} ${row.original_name}`,
    },
    {
      header: "Status",
      headerClassName: "review-col-status",
      cellClassName: "review-cell-status",
      render: (row) => (
        <span className={`document-status ${row.approval_status}`}>
          {row.approval_status}
        </span>
      ),
      searchValue: (row) => row.approval_status,
    },
    {
      header: "Updated",
      headerClassName: "review-col-updated",
      cellClassName: "review-cell-updated",
      render: (row) => (
        <div className="review-updated-cell">
          <span>{row.updated_at || "-"}</span>
        </div>
      ),
      searchValue: (row) => row.updated_at,
    },
    {
      header: "Actions",
      headerClassName: "review-col-actions",
      cellClassName: "review-cell-actions",
      render: (row) => (
        <div className="review-actions">
          {row.is_previewable ? (
            <button
              type="button"
              className="queue-btn"
              onClick={(event) => {
                event.stopPropagation();
                setPreviewDocument(row);
              }}
            >
              Preview
            </button>
          ) : null}
          <button
            type="button"
            className="queue-btn"
            onClick={(event) => {
              event.stopPropagation();
              downloadDocument(row);
            }}
          >
            Download
          </button>
          <button
            type="button"
            className="queue-btn success"
            onClick={(event) => {
              event.stopPropagation();
              updateReview(row, "approved");
            }}
          >
            Approve
          </button>
          <button
            type="button"
            className="queue-btn danger"
            onClick={(event) => {
              event.stopPropagation();
              updateReview(row, "rejected");
            }}
          >
            Reject
          </button>
        </div>
      ),
    },
  ];

  return (
    <MainLayout>
      <div className="employee-documents-page">
        <div className="employee-documents-header">
          <div>
            <h1>Documents</h1>
            <p>Upload and review PAN, Aadhaar, bank proof, and passport documents.</p>
          </div>
          <div className="employee-documents-helper">
            <span>Accepted formats: JPG, JPEG, PDF</span>
            <span>Max size: 1 MB per document</span>
          </div>
        </div>

        <div className="employee-documents-panel">
          <div className="employee-documents-toolbar">
            <div>
              <span className="toolbar-label">Viewing</span>
              <h3>{selectedEmployeeLabel}</h3>
            </div>

            {canManageCompanyDocuments && (
              <label className="employee-selector">
                <span>Select Employee</span>
                <select
                  value={selectedEmployeeId}
                  onChange={(event) => setSelectedEmployeeId(event.target.value)}
                >
                  {meta.employees.map((employee) => (
                    <option key={employee.id} value={employee.id}>
                      {employee.name} ({employee.code})
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>

          {documentData.summary ? (
            <div className="documents-summary-strip">
              {[
                { key: "all", label: "All", value: documentData.summary.total_required },
                { key: "approved", label: "Approved", value: documentData.summary.approved },
                { key: "pending", label: "Pending", value: documentData.summary.pending },
                { key: "rejected", label: "Rejected", value: documentData.summary.rejected },
                { key: "missing", label: "Missing", value: documentData.summary.missing },
              ].map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className={`summary-chip ${statusFilter === item.key ? "active" : ""}`}
                  onClick={() => setStatusFilter(item.key)}
                >
                  <span>{item.label}</span>
                  <strong>{item.value}</strong>
                </button>
              ))}
            </div>
          ) : null}

          {loading ? (
            <div className="documents-empty-state">Loading documents...</div>
          ) : filteredDocuments.length === 0 ? (
            <div className="documents-empty-state">
              No documents matched the current filter.
            </div>
          ) : (
            <div className="documents-grid">
              {filteredDocuments.map((row) => {
                const document = row.document;
                const status = document?.approval_status || "missing";
                const canReview = canReviewCompanyDocuments && !!document;

                return (
                  <div key={row.type} className="document-card">
                    <div className="document-card-top">
                      <div>
                        <h4>{row.label}</h4>
                        <p>
                          {document
                            ? `Last updated ${document.updated_at || "recently"}`
                            : "Document has not been uploaded yet."}
                        </p>
                      </div>
                      <span className={`document-status ${status}`}>
                        {status === "missing" ? "Missing" : status}
                      </span>
                    </div>

                    <div className="document-card-body">
                      <div className="document-meta-list">
                        <div className="document-meta-item">
                          <span>File</span>
                          <strong>{document?.original_name || "No file uploaded"}</strong>
                        </div>
                        <div className="document-meta-item">
                          <span>Size</span>
                          <strong>{document ? formatFileSize(document.file_size) : "N/A"}</strong>
                        </div>
                        <div className="document-meta-item">
                          <span>Review</span>
                          <strong>
                            {document?.approved_by
                              ? `${document.approved_by.first_name || ""} ${document.approved_by.last_name || ""}`.trim()
                              : "Pending"}
                          </strong>
                        </div>
                      </div>

                      {document?.rejection_reason ? (
                        <div className="document-rejection-note">
                          <strong>Rejection reason:</strong> {document.rejection_reason}
                        </div>
                      ) : null}
                    </div>

                    <div className="document-card-actions">
                      <input
                        ref={(element) => {
                          fileInputRefs.current[row.type] = element;
                        }}
                        type="file"
                        accept=".jpg,.jpeg,.pdf"
                        hidden
                        onChange={(event) => handleFileSelected(row.type, event)}
                      />
                      <button
                        type="button"
                        className="doc-btn primary"
                        onClick={() => openFilePicker(row.type)}
                        disabled={savingType === row.type}
                      >
                        <i className="fas fa-upload"></i>
                        {savingType === row.type
                          ? "Uploading..."
                          : document
                            ? "Replace"
                            : "Upload"}
                      </button>

                      {document ? (
                        <>
                          {document.is_previewable ? (
                            <button
                              type="button"
                              className="doc-btn ghost"
                              onClick={() => setPreviewDocument(document)}
                            >
                              <i className="fas fa-eye"></i>
                              Preview
                            </button>
                          ) : null}
                        <button
                          type="button"
                          className="doc-btn secondary"
                          onClick={() => downloadDocument(document)}
                        >
                          <i className="fas fa-download"></i>
                          Download
                        </button>
                        </>
                      ) : null}

                      {canReview ? (
                        <>
                          <button
                            type="button"
                            className="doc-btn success"
                            onClick={() => updateReview(document, "approved")}
                            disabled={reviewingId === document.id}
                          >
                            Approve
                          </button>
                          <button
                            type="button"
                            className="doc-btn danger"
                            onClick={() => updateReview(document, "rejected")}
                            disabled={reviewingId === document.id}
                          >
                            Reject
                          </button>
                        </>
                      ) : null}

                      {document?.can_delete ? (
                        <button
                          type="button"
                          className="doc-btn danger-outline"
                          onClick={() => removeDocument(document)}
                          disabled={reviewingId === document.id}
                        >
                          Remove
                        </button>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {canReviewCompanyDocuments ? (
          <div className="employee-documents-panel review-queue-panel">
            <div className="employee-documents-toolbar">
              <div>
                <span className="toolbar-label">Admin Review</span>
                <h3>Document Review Queue</h3>
              </div>
              {queueMeta ? (
                <div className="review-queue-summary">
                  <span>Pending {queueMeta.pending || 0}</span>
                  <span>Approved {queueMeta.approved || 0}</span>
                  <span>Rejected {queueMeta.rejected || 0}</span>
                </div>
              ) : null}
            </div>

            <div className="review-queue-table-wrap">
              <CommonTable
                columns={reviewQueueColumns}
                data={reviewQueue}
                searchPlaceholder="Search by employee, code, document..."
                emptyMessage="No uploaded documents available for review."
                tableClassName="common-table"
              />
            </div>
          </div>
        ) : null}

        {previewDocument ? (
          <CommonModal
            onClose={() => setPreviewDocument(null)}
            title={previewDocument.original_name}
            overlayClassName="modal-overlay"
            contentClassName="modal-container document-preview-modal"
            headerClassName="modal-header"
            bodyClassName="modal-body document-preview-body"
            closeButtonClassName="modal-close"
          >
            {previewDocument.mime_type?.toLowerCase().includes("pdf") ? (
              <iframe
                title={previewDocument.original_name}
                src={previewDocument.preview_url}
                className="document-preview-frame"
              />
            ) : (
              <img
                src={previewDocument.preview_url}
                alt={previewDocument.original_name}
                className="document-preview-image"
              />
            )}
          </CommonModal>
        ) : null}
      </div>
    </MainLayout>
  );
}

export default EmployeeDocuments;
